import {
    Bot,
    Cog,
    Database,
    Frame,
    Rocket,
    Settings2,
    TerminalSquare,
    Scale,
    Hammer,
    ReceiptText,
    Key,
    Telescope,
    BookOpen,
    Lock,
    ScanText,
    File,
    FileCheck,
    FileText,
    ChartLine,
    ChartColumn,
    CircleDollarSign,
    Tags,
    Pill,
    ClipboardPlus,
    ClipboardCheck,
    ListChecks,
    ShieldAlert,
    Search,
    Settings,
    Brain,
    PanelTop,
    Flag,
} from 'lucide-react';

import type { DashboardNavigation } from 'components/layouts/dashboard-layout';
import {
    MEDICARD_EXTERNAL_ORGANIZATION_ID,
    SALA_AIA_ORGANIZATION_ID,
    ATOMIX_ORGANIZATION_ID,
    ATOMIX_NO_PHI_ORGANIZATION_ID,
    RPM_ORGANIZATION_ID,
    ASTER_ORGANIZATION_ID,
    SALA_MAXICARE_ORGANIZATION_ID,
    ASIAN_HOSPITAL_ORGANIZATION_ID,
} from 'components/layouts/dashboard-layout/constants';
import type { User } from 'types/api';

const EXCLUDED_ORGANIZATIONS = [
    MEDICARD_EXTERNAL_ORGANIZATION_ID,
    ASTER_ORGANIZATION_ID,
    RPM_ORGANIZATION_ID,
    SALA_MAXICARE_ORGANIZATION_ID,
    SALA_AIA_ORGANIZATION_ID,
    ASIAN_HOSPITAL_ORGANIZATION_ID,
];

export const platformDashboardNavigation: DashboardNavigation = {
    navMain: [
        {
            title: 'Projects',
            url: '/app/projects',
            icon: Bot,
            isActive: true,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Datasets',
            url: '/app/datasets',
            icon: Database,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Graders',
            url: '/app/graders',
            icon: Scale,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Evaluations',
            url: '/app/evaluations',
            icon: ClipboardCheck,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Tools',
            url: '/app/tools',
            icon: Hammer,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Deployments',
            url: '/app/deployments',
            icon: Rocket,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Telemetry',
            url: '/app/telemetry',
            icon: Telescope,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Claims Lookup',
            url: '/app/medicard-claims',
            icon: File,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'Insurance Analytics',
            url: '/app/insurance-analytics',
            icon: ChartLine,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    ATOMIX_NO_PHI_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Medication Analytics',
            url: '/app/medication-analytics',
            icon: Pill,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    MEDICARD_EXTERNAL_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Medicard POC grading',
            url: '/app/medicard-poc-grading',
            icon: ListChecks,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    MEDICARD_EXTERNAL_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Medical Code Lookup',
            url: '/app/medical-code-lookup',
            icon: Search,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'OCR Labeling',
            url: '/app/ocr-labeling',
            icon: PanelTop,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'PhilHealth Command Center',
            url: '/app/philhealth-claim-automation',
            icon: FileCheck,
            condition: (user: User | undefined) =>
                user?.organization.id === ASIAN_HOSPITAL_ORGANIZATION_ID,
        },
        {
            title: 'CPT Code Configuration',
            url: '/app/maxicare-cpt-mapping',
            icon: Settings2,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_MAXICARE_ORGANIZATION_ID,
        },
        {
            title: 'Transcript Extraction',
            url: '/app/transcript-demo',
            icon: FileText,
            condition: (user: User | undefined) =>
                user?.organization.id === SALA_AIA_ORGANIZATION_ID,
        },
        {
            title: 'Claims AI',
            url: '/app/insurance-extraction/tasks',
            icon: ClipboardPlus,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    SALA_MAXICARE_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Field Configuration',
            url: '/app/insurance-extraction-config',
            icon: Settings,
            condition: (user: User | undefined) =>
                [SALA_MAXICARE_ORGANIZATION_ID].includes(
                    user?.organization.id || '',
                ),
        },
        {
            title: 'Claim Analytics',
            url: '/app/claim-analytics',
            icon: ChartLine,
            condition: (user: User | undefined) =>
                [
                    SALA_AIA_ORGANIZATION_ID,
                    SALA_MAXICARE_ORGANIZATION_ID,
                    MEDICARD_EXTERNAL_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'FWA Detection',
            url: '/app/fwa-dashboard',
            icon: ShieldAlert,
            condition: (user: User | undefined) =>
                [
                    SALA_MAXICARE_ORGANIZATION_ID,
                    SALA_AIA_ORGANIZATION_ID,
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Entity Extraction',
            url: '/app/entity-extraction',
            icon: Tags,
            condition: (user: User | undefined) =>
                [
                    ATOMIX_ORGANIZATION_ID,
                    ATOMIX_NO_PHI_ORGANIZATION_ID,
                ].includes(user?.organization?.id || ''),
        },
        {
            title: 'RPM Invoice Extraction',
            url: '/app/rpm',
            icon: ReceiptText,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Payroll Vendors',
            url: '/app/rpm/vendors',
            icon: Settings2,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Extraction Rules',
            url: '/app/rpm/rules',
            icon: Scale,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Exception Flags',
            url: '/app/rpm/exception-flags',
            icon: Flag,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Vendor Analytics',
            url: '/app/rpm/vendor-analytics',
            icon: ChartColumn,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'Usage billing',
            url: '/app/rpm/billing',
            icon: CircleDollarSign,
            condition: (user: User | undefined) =>
                [RPM_ORGANIZATION_ID].includes(user?.organization?.id || '') &&
                user?.is_stytch_admin === true,
        },
        {
            title: 'Invoice Extraction',
            url: '/app/aster-invoice-extraction',
            icon: File,
            condition: (user: User | undefined) =>
                [ASTER_ORGANIZATION_ID].includes(user?.organization?.id || ''),
        },
        {
            title: 'API Keys',
            url: '/app/api-keys',
            icon: Key,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Secrets',
            url: '/app/secrets',
            icon: Lock,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'OCR',
            url: '/app/ocr',
            icon: ScanText,
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'Documentation',
            icon: BookOpen,
            url: '/app/documentation',
            items: [
                {
                    title: 'Introduction',
                    url: '/app/documentation/introduction',
                },
                {
                    title: 'Projects Guide',
                    url: '/app/documentation/projects',
                },
                {
                    title: 'Datasets Guide',
                    url: '/app/documentation/datasets',
                },
                {
                    title: 'Graders Guide',
                    url: '/app/documentation/graders',
                },
                {
                    title: 'Tools Guide',
                    url: '/app/documentation/tools',
                },
                {
                    title: 'Implementations Guide',
                    url: '/app/documentation/implementations',
                },
                {
                    title: 'Deployments Guide',
                    url: '/app/documentation/deployments',
                },
                {
                    title: 'Telemetry Guide',
                    url: '/app/documentation/telemetry',
                },
                {
                    title: 'Secrets Guide',
                    url: '/app/documentation/secrets',
                },
                {
                    title: 'OCR Guide',
                    url: '/app/documentation/ocr',
                },
                {
                    title: 'Data Quality Review Guide',
                    url: '/app/documentation/data-quality-review',
                },
                {
                    title: 'API Documentation',
                    url: '/app/documentation/api',
                },
                {
                    title: 'Model Pricing',
                    url: '/app/documentation/model-pricing',
                },
            ],
            condition: (user: User | undefined) =>
                !EXCLUDED_ORGANIZATIONS.includes(user?.organization.id || ''),
        },
        {
            title: 'AI Analytics',
            url: '/app/analytics-ai',
            icon: Brain,
            condition: (user: User | undefined) =>
                [MEDICARD_EXTERNAL_ORGANIZATION_ID].includes(
                    user?.organization.id || '',
                ),
        },
    ],
    navSecondary: [
        {
            title: 'Settings',
            url: '/app/settings',
            icon: Cog,
        },
    ],
    projects: [
        {
            name: 'Overview',
            url: '/app/projects/:projectId',
            icon: Frame,
        },
        {
            name: 'Configuration',
            url: '/app/projects/:projectId/configuration',
            icon: Settings2,
        },
        {
            name: 'Data Quality Review',
            url: '/app/projects/:projectId/data-quality-review',
            icon: FileCheck,
        },
        {
            name: 'Implementations',
            url: '/app/projects/:projectId/implementations',
            icon: TerminalSquare,
        },
    ],
};
